import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Info, ExternalLink, ChevronLeft, ChevronRight, X, MapPin, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import Viewer360 from "../components/Viewer360";

// Tour Data
const toursData = {
  poggio: {
    name: "Poggio alle Lame",
    subtitle: "Agriturismo & Camere",
    ctaText: "Prenota Ora",
    ctaUrl: "https://www.poggioallelame.it/it/contatti-e-prenota/",
    rooms: [
      { id: "room1", name: "Ingresso", description: "Benvenuti a Poggio alle Lame. L'ingresso principale vi accoglie con calore toscano.", image: "/360/poggio/room1.jpg" },
      { id: "room2", name: "Soggiorno", description: "Un ampio soggiorno con arredi rustici e vista sulla campagna.", image: "/360/poggio/room2.jpg" },
      { id: "room3", name: "Camera Matrimoniale", description: "Camera spaziosa con letto matrimoniale e bagno privato.", image: "/360/poggio/room3.jpg" },
      { id: "room4", name: "Camera Deluxe", description: "La nostra suite più esclusiva con terrazza panoramica.", image: "/360/poggio/room4.jpg" },
      { id: "room5", name: "Giardino", description: "Il giardino esterno con piscina e area relax.", image: "/360/poggio/room5.jpg" },
    ]
  },
  openstudio: {
    name: "Studio Legale",
    subtitle: "Consulenza Professionale",
    ctaText: "Contatta",
    ctaUrl: "https://www.openstudio.solutions/professionisti/claudio-volterrani/",
    rooms: [
      { id: "room1", name: "Reception", description: "L'area reception dello studio professionale.", image: "/360/openstudio/room1.jpg" },
      { id: "room2", name: "Open Space", description: "L'ampio open space per meeting e collaborazioni.", image: "/360/openstudio/room2.jpg" },
      { id: "room3", name: "Sala Riunioni", description: "Sala riunioni attrezzata per videoconferenze.", image: "/360/openstudio/room3.jpg" },
      { id: "room4", name: "Ufficio Privato", description: "Ufficio privato per consulenze riservate.", image: "/360/openstudio/room4.jpg" },
    ]
  }
};

export default function TourViewer() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showHUD, setShowHUD] = useState(false);

  const tour = toursData[tourId];
  
  useEffect(() => {
    if (!tour) {
      navigate("/");
    }
  }, [tour, navigate]);

  // Show HUD after viewer loads
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => setShowHUD(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const currentRoom = tour?.rooms[currentRoomIndex];

  const goToRoom = useCallback((index) => {
    if (index >= 0 && index < tour.rooms.length) {
      setCurrentRoomIndex(index);
      setShowInfo(false);
    }
  }, [tour?.rooms.length]);

  const nextRoom = useCallback(() => {
    goToRoom((currentRoomIndex + 1) % tour.rooms.length);
  }, [currentRoomIndex, tour?.rooms.length, goToRoom]);

  const prevRoom = useCallback(() => {
    goToRoom((currentRoomIndex - 1 + tour.rooms.length) % tour.rooms.length);
  }, [currentRoomIndex, tour?.rooms.length, goToRoom]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") nextRoom();
      if (e.key === "ArrowLeft") prevRoom();
      if (e.key === "Escape") navigate("/");
      if (e.key === "i" || e.key === "I") setShowInfo(prev => !prev);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextRoom, prevRoom, navigate]);

  if (!tour) return null;

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-[#080A0E]" data-testid="tour-viewer">
      {/* Loading Screen */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            className="loading-screen"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="loading-spinner mb-4" />
            <p className="text-[#A1A5B0] text-sm tracking-wider">Caricamento tour...</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 360 Viewer */}
      <div className="viewer-container">
        <Viewer360
          image={currentRoom.image}
          onLoad={() => setIsLoading(false)}
        />
      </div>

      {/* HUD Overlay */}
      <AnimatePresence>
        {showHUD && (
          <>
            {/* Top Bar */}
            <motion.div
              className="hud-top crystal-glass rounded-b-xl"
              initial={{ y: -100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -100, opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center justify-between">
                {/* Back Button & Title */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => navigate("/")}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    data-testid="back-button"
                  >
                    <ArrowLeft className="w-5 h-5 text-white" />
                  </button>
                  <div>
                    <h1 className="font-heading text-xl text-white">{tour.name}</h1>
                    <p className="text-xs text-[#A1A5B0]">{tour.subtitle}</p>
                  </div>
                </div>

                {/* Room Name & Navigation */}
                <div className="hidden sm:flex items-center gap-4">
                  <button
                    onClick={prevRoom}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    data-testid="prev-room-btn"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <span className="text-white font-medium min-w-[120px] text-center">
                    {currentRoom.name}
                  </span>
                  <button
                    onClick={nextRoom}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    data-testid="next-room-btn"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Info Button */}
                <button
                  onClick={() => setShowInfo(true)}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                  data-testid="info-button"
                >
                  <Info className="w-5 h-5 text-white" />
                </button>
              </div>
            </motion.div>

            {/* Bottom Gallery */}
            <motion.div
              className="hud-bottom crystal-glass rounded-t-xl"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {tour.rooms.map((room, index) => (
                  <div
                    key={room.id}
                    onClick={() => goToRoom(index)}
                    className={`room-thumb w-28 h-20 sm:w-32 sm:h-24 ${index === currentRoomIndex ? 'active' : ''}`}
                    data-testid={`room-thumb-${room.id}`}
                  >
                    <img src={room.image} alt={room.name} loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <span className="absolute bottom-2 left-2 text-xs text-white font-medium">
                      {room.name}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              className="hud-cta"
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <a
                href={tour.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button"
                data-testid="book-now-btn"
              >
                {tourId === 'poggio' ? <Phone className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                {tour.ctaText}
                <ExternalLink className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Mobile Navigation */}
            <motion.div
              className="fixed bottom-28 left-1/2 -translate-x-1/2 sm:hidden flex items-center gap-4 z-50"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <button
                onClick={prevRoom}
                className="p-3 rounded-full crystal-glass"
                data-testid="mobile-prev-btn"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <span className="text-white text-sm font-medium px-4 py-2 crystal-glass rounded-full">
                {currentRoomIndex + 1} / {tour.rooms.length}
              </span>
              <button
                onClick={nextRoom}
                className="p-3 rounded-full crystal-glass"
                data-testid="mobile-next-btn"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Info Dialog */}
      <Dialog open={showInfo} onOpenChange={setShowInfo} modal={true}>
        <DialogContent 
          className="crystal-glass border-white/10 text-white max-w-md"
          onPointerDownOutside={() => setShowInfo(false)}
          onEscapeKeyDown={() => setShowInfo(false)}
          data-testid="info-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white flex items-center gap-2">
              <Info className="w-5 h-5 text-[#D4AF37]" />
              {currentRoom.name}
            </DialogTitle>
            <DialogDescription className="text-[#A1A5B0] pt-4 text-base leading-relaxed">
              {currentRoom.description}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6 flex gap-3">
            <Button
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={() => setShowInfo(false)}
              data-testid="close-info-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Chiudi
            </Button>
            <a
              href={tour.ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#F0C855]" data-testid="dialog-cta-btn">
                {tour.ctaText}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
